// The live OT display board (OT_0030).
//
// Every case booked for the day, in theatre order, showing where each patient
// physically is. Polled, not pushed: there are no websockets in this stack, and
// a board that refreshes every twenty seconds is indistinguishable from one that
// is pushed to.
//
// The board shows tracking status, which is independent of lifecycle status
// (ADR-0003) — a case is "final" on the list and "Checked In" physically at the
// same time.
//
// Laid out as ROWS, matching the Emergency tracking board, because they answer
// the same question about the same kind of person and there is no reason for
// them to look like different products.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import moment from "moment";

import {
  IPD_COLORS,
  IPD_SHAPE,
  IpdDateField,
  IpdStatCard,
  ipdCardSx,
} from "../../ipd/utils";
import { formatIstTime } from "../../../utils/istDateTime";
import { fetchBoard, moveTracking } from "../api/otApi";
import OtScreen from "../components/OtScreen";
import { FirmnessChip, TrackingChip, errorMessage, report } from "../components/common";
import { OT_BASE, TRACKING_LABELS, trackingTone } from "../constants";

const REFRESH_MS = 20000;

const COLUMNS = "minmax(180px, 1.4fr) 150px minmax(160px, 1.2fr) 150px 132px 190px";

/** The four bands the nine rungs collapse into — see TRACKING_TONE. */
const BANDS = {
  toCome: ["AWAITING_CALL", "TO_CALL", "CALLED"],
  inSuite: ["CHECKED_IN", "AWAITING_PROCEDURE"],
  inTheatre: ["IN_PROGRESS"],
  through: ["COMPLETED", "IN_RECOVERY", "CHECKED_OUT"],
};

function BoardColumnHeader() {
  return (
    <Box
      sx={{
        display: { xs: "none", lg: "grid" },
        gridTemplateColumns: COLUMNS,
        gap: 2,
        px: 2.5,
        pb: 1,
        pl: 3,
      }}
    >
      {["Patient", "Theatre", "Procedure", "Surgeon", "List", ""].map((h, i) => (
        <Typography
          key={h || `spacer-${i}`}
          sx={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.6,
            textTransform: "uppercase",
            color: IPD_COLORS.textSecondary,
          }}
        >
          {h}
        </Typography>
      ))}
    </Box>
  );
}

/** The coloured spine down the left edge of a row, as Emergency does it. */
function TrackingStripe({ status }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        width: 4,
        borderTopLeftRadius: `${IPD_SHAPE.radiusMd}px`,
        borderBottomLeftRadius: `${IPD_SHAPE.radiusMd}px`,
        bgcolor: trackingTone(status).color,
      }}
    />
  );
}

function BoardRow({ row, onOpen, onMove }) {
  // Forward-only, one rung at a time, so there is at most one move available.
  // Rendering it as "→ Called" rather than "Called" is the difference between a
  // button and a second copy of the status chip beside it.
  const next = (row.allowed_tracking_transitions || [])[0];

  return (
    <Box
      role="link"
      tabIndex={0}
      aria-label={`Open ${row.patient_name || "this case"}`}
      onClick={() => onOpen(row)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen(row);
        }
      }}
      sx={{
        ...ipdCardSx,
        position: "relative",
        display: "grid",
        gridTemplateColumns: { xs: "1fr", lg: COLUMNS },
        alignItems: "center",
        gap: { xs: 1, lg: 2 },
        p: 2,
        pl: 3,
        cursor: "pointer",
        transition: "box-shadow .2s ease, border-color .2s ease",
        "&:hover": {
          borderColor: IPD_COLORS.borderHover,
          boxShadow: IPD_COLORS.shadow.cardHover,
        },
        "&:focus-visible": { outline: `2px solid ${IPD_COLORS.primary}`, outlineOffset: 2 },
      }}
    >
      <TrackingStripe status={row.tracking_started ? row.tracking_status : null} />

      {/* Patient */}
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: 14,
              color: IPD_COLORS.textPrimary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.patient_name || "Unnamed"}
          </Typography>
          {row.biohazard && (
            <Box
              sx={{
                px: 0.7,
                borderRadius: 0.75,
                fontSize: 10,
                fontWeight: 800,
                letterSpacing: "0.04em",
                bgcolor: IPD_COLORS.error.bg,
                color: IPD_COLORS.error.text,
                border: `1px solid ${IPD_COLORS.error.border}`,
              }}
            >
              BIO
            </Box>
          )}
        </Box>
        <Typography sx={{ mt: 0.25, fontSize: 12, color: IPD_COLORS.textSecondary }}>
          {row.case_number}
          {row.health_record_id ? ` · ${row.health_record_id}` : ""}
        </Typography>
      </Box>

      {/* Theatre and time */}
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: IPD_COLORS.textPrimary }}>
          {row.operating_room_name}
        </Typography>
        <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
          {formatIstTime(row.start_datetime)}–{formatIstTime(row.end_datetime)}
        </Typography>
      </Box>

      {/* Procedure */}
      <Typography
        sx={{
          fontSize: 13,
          color: row.procedure_name ? IPD_COLORS.textPrimary : IPD_COLORS.textMuted,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {row.procedure_name || "Not set"}
      </Typography>

      {/* Surgeon */}
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 13,
            color: row.primary_surgeon_name ? IPD_COLORS.textPrimary : IPD_COLORS.textMuted,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {row.primary_surgeon_name || "Unassigned"}
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: IPD_COLORS.textMuted }}>
          {row.scheduling_type}
        </Typography>
      </Box>

      <Box><FirmnessChip firmness={row.firmness} /></Box>

      {/* Where they are, and the one move available from here. */}
      <Box
        sx={{ display: "flex", gap: 1, alignItems: "center", justifyContent: "flex-end" }}
        onClick={(event) => event.stopPropagation()}
      >
        {/* Null until somebody puts the patient on the board, and
            TrackingChip renders that as "Not started" — which is the
            difference between a state and the button offering to set it. */}
        <TrackingChip status={row.tracking_started ? row.tracking_status : null} />
        {next && (
          <Button
            size="small"
            variant="outlined"
            endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
            onClick={() => onMove(row, next)}
            sx={{ whiteSpace: "nowrap" }}
          >
            {TRACKING_LABELS[next] || next}
          </Button>
        )}
      </Box>
    </Box>
  );
}

/**
 * The ladder, as a filter rather than a scoreboard.
 *
 * Nine tiles of which eight read zero is eight things to look at and one worth
 * seeing. As a filter strip the same nine earn their space: they say what the
 * day looks like AND narrow the board to one rung.
 */
function LadderFilter({ counts, active, onPick }) {
  return (
    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 2.5 }}>
      {counts.map((c) => {
        const selected = active === c.status;
        const empty = !c.count;
        const tone = trackingTone(c.status);
        return (
          <Box
            key={c.status}
            role="button"
            tabIndex={empty ? -1 : 0}
            aria-pressed={selected}
            onClick={() => !empty && onPick(selected ? null : c.status)}
            onKeyDown={(event) => {
              if (!empty && (event.key === "Enter" || event.key === " ")) {
                event.preventDefault();
                onPick(selected ? null : c.status);
              }
            }}
            sx={{
              display: "flex",
              alignItems: "baseline",
              gap: 0.75,
              px: 1.25,
              py: 0.6,
              borderRadius: `${IPD_SHAPE.radiusMd}px`,
              cursor: empty ? "default" : "pointer",
              userSelect: "none",
              border: `1px solid ${selected ? tone.border : IPD_COLORS.border}`,
              bgcolor: selected ? tone.bg : IPD_COLORS.cardBg,
              opacity: empty ? 0.45 : 1,
              transition: "background-color .15s ease, border-color .15s ease",
              "&:hover": empty ? {} : { borderColor: IPD_COLORS.borderHover },
            }}
          >
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: selected ? 700 : 500,
                color: selected ? tone.text : IPD_COLORS.textSecondary,
              }}
            >
              {c.label}
            </Typography>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 700,
                color: c.count ? IPD_COLORS.textPrimary : IPD_COLORS.textMuted,
              }}
            >
              {c.count}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

export default function OtBoard() {
  const history = useHistory();
  const [date, setDate] = useState(moment().format("YYYY-MM-DD"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [live, setLive] = useState(true);
  const [rung, setRung] = useState(null);
  const timer = useRef(null);

  const load = useCallback(async (day, quiet) => {
    if (!quiet) setLoading(true);
    try {
      setData(await fetchBoard({ date: day }));
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Could not load the board"));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(date);
  }, [load, date]);

  // Quiet refresh: the board is a wall display, and a spinner every twenty
  // seconds would make it unreadable.
  useEffect(() => {
    if (!live) return undefined;
    timer.current = setInterval(() => load(date, true), REFRESH_MS);
    return () => clearInterval(timer.current);
  }, [live, date, load]);

  const cases = useMemo(() => data?.cases || [], [data]);
  const counts = data?.counts_by_tracking_status || [];

  const band = useCallback(
    (rungs) => cases.filter((c) => rungs.includes(c.tracking_status)).length,
    [cases]
  );

  const shown = rung ? cases.filter((c) => c.tracking_status === rung) : cases;

  const move = async (row, to) => {
    if (report(await moveTracking(row.ot_case_id, to))) load(date, true);
  };

  return (
    <OtScreen
      title="OT Tracking Board"
      description="Where every patient on today's list physically is."
      badge={data?.count}
      loading={loading}
      error={error}
      onRetry={() => load(date)}
      actions={
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Button
            size="small"
            variant={live ? "contained" : "outlined"}
            onClick={() => setLive((v) => !v)}
          >
            {live ? "Live" : "Paused"}
          </Button>
          <Box sx={{ width: 170 }}>
            <IpdDateField value={date} onChange={(iso) => iso && setDate(iso)} />
          </Box>
        </Box>
      }
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", lg: "repeat(4, 1fr)" },
          gap: 2,
          mb: 2.5,
        }}
      >
        <IpdStatCard
          label="On today's list"
          value={cases.length}
          icon={FormatListBulletedIcon}
          loading={loading}
        />
        <IpdStatCard
          label="Still to come"
          value={band(BANDS.toCome)}
          icon={HourglassTopIcon}
          tone="amber"
          loading={loading}
        />
        <IpdStatCard
          label="In theatre now"
          value={band(BANDS.inTheatre)}
          icon={MonitorHeartIcon}
          tone="red"
          loading={loading}
        />
        <IpdStatCard
          label="Through"
          value={band(BANDS.through)}
          icon={CheckCircleOutlineIcon}
          tone="mint"
          loading={loading}
        />
      </Box>

      <LadderFilter counts={counts} active={rung} onPick={setRung} />

      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, mb: 1 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
          {moment(date, "YYYY-MM-DD").format("dddd, D MMMM YYYY")}
        </Typography>
        {rung && (
          <Button size="small" onClick={() => setRung(null)}>
            Clear filter
          </Button>
        )}
      </Box>

      <BoardColumnHeader />

      {shown.length === 0 ? (
        <Box sx={{ ...ipdCardSx, p: 4, textAlign: "center" }}>
          <Typography sx={{ fontSize: 13.5, color: IPD_COLORS.textSecondary }}>
            {rung
              ? `Nobody is at ${TRACKING_LABELS[rung]} right now.`
              : "Nothing on the board for this day."}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "grid", gap: 1 }}>
          {shown.map((row) => (
            <BoardRow
              key={row.ot_case_id}
              row={row}
              onOpen={(r) => history.push(`${OT_BASE}/case/${r.ot_case_id}`)}
              onMove={move}
            />
          ))}
        </Box>
      )}
    </OtScreen>
  );
}
